import { H3 } from "@sellar-io/components";
import { SupplierRelationshipGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useNavigate } from "react-router";
import { SpinnerLegacy } from "../../components";
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import { IntersectingInfiniteLoadingButton } from "../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../components/table/TableLegacy";
import { CUSTOMER_ORDER } from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useSupplierOrderList } from "../../hooks/api/v2/order/useSupplierOrderList";
import OrderStatusIndicator from "../order/orderStatusIndicator/OrderStatusIndicator";

export const CustomerOrderList = ({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}): ReactElement => {
  const navigate = useNavigate();

  const orderListQuery = useSupplierOrderList({
    query: [
      {
        type: "RetailerId",
        modifier: "EQUALS",
        value: connection.BuyerId ?? 0,
      },
    ],
    limit: 10,
  });

  if (orderListQuery.isLoading) return <SpinnerLegacy />;

  if (orderListQuery.isError)
    return (
      <ErrorMessage>
        {apiV2AssertError(orderListQuery.error)?.response?.data.message ??
          "An unknown error occurred."}
      </ErrorMessage>
    );

  return (
    <>
      {!!orderListQuery.data?.pages[0]?.length && (
        <div className={"flex flex-col gap-2"}>
          <H3>Order history</H3>
          <TableLegacyContainer>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR>
                  <TableLegacyTH>Order no.</TableLegacyTH>
                  <TableLegacyTH>Placed on</TableLegacyTH>
                  <TableLegacyTH>Total</TableLegacyTH>
                  <TableLegacyTH>Status</TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {orderListQuery.data.pages.map((page) =>
                  page.map((order, i) => (
                    <TableLegacyTR
                      className={"cursor-pointer"}
                      key={i}
                      onClick={() =>
                        navigate(
                          CUSTOMER_ORDER.href({
                            connectionId: connection.id,
                            orderId: order.id,
                          }),
                        )
                      }
                    >
                      <TableLegacyTD>#{order.id}</TableLegacyTD>
                      <TableLegacyTD>
                        {dayjs(order.createdAt).format("DD/MM/YYYY")}
                      </TableLegacyTD>
                      <TableLegacyTD className={"font-bold"}>
                        {formatCurrency(order.price)}
                      </TableLegacyTD>
                      <TableLegacyTD>
                        <OrderStatusIndicator
                          order={{
                            ...order,
                            Supplier: {
                              ...order.Supplier,
                              Business: {
                                ...order.Supplier.Business,
                                id: parseInt(
                                  order.Supplier.Business.id.toString(),
                                ),
                              },
                            },
                            Retailer: {
                              ...order.Retailer,
                              Business: {
                                ...order.Retailer.Business,
                                id: parseInt(
                                  order.Retailer.Business.id.toString(),
                                ),
                              },
                            },
                          }}
                        />
                      </TableLegacyTD>
                    </TableLegacyTR>
                  )),
                )}
              </TableLegacyTBody>
            </TableLegacy>
            <IntersectingInfiniteLoadingButton query={orderListQuery} />
          </TableLegacyContainer>
        </div>
      )}
    </>
  );
};
